import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { BASE_URL } from '../constants/config';

/**
 * Hook that checks whether the current patient already has an upcoming
 * free consultation booked.  Returns helpers to run the check, dismiss it,
 * or cancel the previous consultation and proceed.
 *
 * Usage:
 *   const { checkAndProceed, ... } = useFreeConsultationCheck();
 *
 *   const handleBookClick = () => checkAndProceed(() => openModal(...));
 */
const useFreeConsultationCheck = () => {
  const patientData = useSelector((state) => state.patientData?.data);

  const [showDialog, setShowDialog] = useState(false);
  const [existingConsultation, setExistingConsultation] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Stash the "proceed" callback so we can call it after the dialog decision.
  const [proceedCallback, setProceedCallback] = useState(null);

  /**
   * Run the free-consultation check.  If the patient has no prior booking
   * (or we can't determine their email) we invoke `onProceed` immediately.
   * Otherwise we surface the warning dialog and let the user decide.
   */
  const checkAndProceed = useCallback(
    async (onProceed) => {
      const email = patientData?.email?.trim().toLowerCase();

      if (!email) {
        // No email available – skip the check and proceed directly
        onProceed();
        return;
      }

      try {
        const response = await axios.post(`${BASE_URL}/user/check-free-consultation`, {
          email,
        });

        if (response.data?.hasConsultation && response.data?.appointment) {
          setExistingConsultation(response.data.appointment);
          setProceedCallback(() => onProceed);
          setShowDialog(true);
          return;
        }
      } catch (err) {
        // Fail-open: if the check fails, let the user proceed normally
        console.warn('Free consultation check failed:', err.message);
      }

      onProceed();
    },
    [patientData],
  );

  /** Dismiss the dialog and proceed anyway (user chose "Go Back" = proceed
   *  without cancelling the existing consultation). */
  const dismiss = useCallback(() => {
    setShowDialog(false);
    setExistingConsultation(null);
    if (proceedCallback) proceedCallback();
    setProceedCallback(null);
  }, [proceedCallback]);

  /** Cancel the previous consultation, then proceed. */
  const cancelAndProceed = useCallback(async () => {
    if (!existingConsultation) return;

    setCancelling(true);
    try {
      await axios.put(`${BASE_URL}/gohighlevel/events/${existingConsultation.eventId}`, {
        calendarId: existingConsultation.calendarId,
        contactId: existingConsultation.contactId,
        assignedUserId: existingConsultation.assignedUserId,
        cancelledBy: 'patient',
        startTime: existingConsultation.startTime,
        endTime: existingConsultation.endTime,
        title: existingConsultation.title,
        appointmentStatus: 'cancelled',
      });
    } catch (cancelError) {
      console.error('Failed to cancel consultation:', cancelError.message);
      // Still proceed even if cancellation fails – don't block the user
    }

    setCancelling(false);
    setShowDialog(false);
    setExistingConsultation(null);
    if (proceedCallback) proceedCallback();
    setProceedCallback(null);
  }, [existingConsultation, proceedCallback]);

  return {
    showDialog,
    cancelling,
    checkAndProceed,
    dismiss,
    cancelAndProceed,
  };
};

export default useFreeConsultationCheck;
